import React, { useState, useEffect } from "react";
// @material-ui/core components
import { Dialog, makeStyles, useMediaQuery, useTheme, CircularProgress } from "@material-ui/core";
// core components
import GridItem from "../../components/Grid/GridItem.js";
import GridContainer from "../../components/Grid/GridContainer.js";
import CustomInput from "../../components/CustomInput/CustomInput.js";
import Button from "../../components/CustomButtons/Button.js";
import Card from "../../components/Card/Card.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardBody from "../../components/Card/CardBody.js";
import CardFooter from "../../components/Card/CardFooter.js";
import axios from "axios";
import { UpdateFittingMasterAPI, baseUrl } from "../../config/api";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FilePicker } from "../../components/common/FilePicker.js";

const styles = {
  formControl: {
    paddingBottom: "10px",
    margin: "27px 0 0 0",
    position: "relative",
    verticalAlign: "unset",
  },
  cardCategoryWhite: {
    color: "rgba(255,255,255,.62)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0",
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
  },
};

const useStyles = makeStyles(styles);

export default function FittingMasterEdit(props) {
  const { item } = props;
  let authToken = useSelector((state) => state.userReducer.authToken);
  let userInfo = useSelector((state) => state.userReducer.userInfo);
  const classes = useStyles();
  const theme = useTheme();
  const { open, close, fetchData } = props;
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const [fittingType, setFittingType] = useState(
    item?.fittingType ? item.fittingType : ""
  );
  const [amount, setAmount] = useState(item?.amount ? item.amount : "");
  const [image, setImage] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize image state with existing image if available
  useEffect(() => {
    if (item?.image) {
      // Construct full URL for existing image (similar to BankEdit)
      const imagePath = item.image.startsWith('/')
        ? `${baseUrl}${item.image}`
        : `${baseUrl}/uploads/fittingMasters/${item.image}`;
      setImage([imagePath]);
    } else {
      setImage([]);
    }
  }, [item]);

  async function _onSave() {
    if (!fittingType || !amount) {
      toast.error("Please fill all required fields", { position: "top-center" });
      return;
    }

    const formData = new FormData();
    formData.append("fittingType", fittingType.trim());
    formData.append("amount", amount.trim());

    // Only append image if a new file was selected (File object)
    // FilePicker can have: string (existing image URL) or object with .path (new file)
    if (image.length > 0) {
      const imageFile = image[0];
      // Check if it's a new file (File object) - existing images are strings
      if (typeof imageFile === 'object' && imageFile.path instanceof File) {
        formData.append("image", imageFile.path);
      }
      // If imageFile is a string, it's an existing image URL - don't append (keeps existing)
    }

    setIsSubmitting(true);
    try {
      await axios.patch(UpdateFittingMasterAPI(item._id), formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      });

      toast.success("Successfully Updated!", { position: "top-center" });
      
      // Refresh data first, then close dialog
      if (fetchData && typeof fetchData === 'function') {
        await fetchData();
      }
      
      close();
    } catch (e) {
      toast.error("Something went wrong, please contact admin", {
        position: "top-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={close}
      aria-labelledby="responsive-dialog-title"
    >
      <GridContainer>
        <GridItem xs={12} sm={12}>
          <Card>
            <CardHeader color="primary">
              <h4 className={classes.cardTitleWhite}>Update Fitting Master</h4>
            </CardHeader>
            <CardBody>
              <GridContainer>
                {/* Image Upload */}
                <GridItem xs={12}>
                  <FilePicker
                    files={image}
                    buttonLabel={"Upload/Update Fitting Photo"}
                    id={"fitting-image-edit"}
                    maxFiles={1}
                    setNewFiles={setImage}
                    filetype={".png,.jpg,.jpeg,.webp"}
                  />
                </GridItem>

                <GridItem xs={6} sm={6} md={6}>
                  <CustomInput
                    labelText="Fitting Type"
                    id="fittingType"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: fittingType,
                      onChange: (event) => setFittingType(event.target.value),
                    }}
                  />
                </GridItem>
                <GridItem xs={6} sm={6} md={6}>
                  <CustomInput
                    labelText="Amount"
                    id="amount"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      type: "number",
                      value: amount,
                      onChange: (event) => setAmount(event.target.value),
                    }}
                  />
                </GridItem>
              </GridContainer>
            </CardBody>
            <CardFooter style={{ justifyContent: "center" }}>
              <Button
                color="primary"
                onClick={() => _onSave()}
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} /> : "Update Fitting Master"}
              </Button>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    </Dialog>
  );
}
